import { createContext, useContext, useState, ReactNode } from 'react'

type AuthFormType = 'signup' | 'login'

interface AuthModalContextValue {
	openAuthModal: (
		type?: AuthFormType | null,
		initialEmail?: string | null,
		redirectTo?: string | null,
		onSuccess?: () => any,
		hideCloseButton?: boolean
	) => void
	closeAuthModal: () => void
	isAuthModalOpen: boolean
	initialEmail: string | undefined
	initialForm: AuthFormType
	redirectTo: string | null
	onSuccessCallback: (() => any) | undefined
	hideCloseButton: boolean
}

const AuthModalContext = createContext<AuthModalContextValue | undefined>(undefined)

export function AuthModalProvider({ children }: { children: ReactNode }) {
	const [isOpen, setIsOpen] = useState(false)
	const [initialEmail, setInitialEmail] = useState<string | undefined>(undefined)
	const [initialForm, setInitialForm] = useState<AuthFormType>('signup')
	const [redirectTo, setRedirectTo] = useState<string | null>(null)
	const [onSuccessCallback, setOnSuccessCallback] = useState<(() => any) | undefined>(undefined)
	const [hideCloseButton, setHideCloseButton] = useState(false)

	const openAuthModal = (
		type: AuthFormType | null = 'signup',
		email?: string | null,
		redirectPath?: string | null,
		onSuccess?: () => any,
		hideClose?: boolean
	) => {
		setInitialForm(type || 'signup')
		setInitialEmail(email || '')
		setRedirectTo(redirectPath || null)
		setHideCloseButton(hideClose ?? false)

		setOnSuccessCallback(() => () => {
			onSuccess?.()
		})
		setIsOpen(true)
	}

	const closeAuthModal = () => {
		setIsOpen(false)
	}

	return (
		<AuthModalContext.Provider
			value={{
				openAuthModal,
				closeAuthModal,
				isAuthModalOpen: isOpen,
				initialEmail,
				initialForm,
				redirectTo,
				onSuccessCallback,
				hideCloseButton,
			}}
		>
			{children}
		</AuthModalContext.Provider>
	)
}

export function useAuthModal() {
	const context = useContext(AuthModalContext)
	if (context === undefined) {
		throw new Error('useAuthModal must be used within an AuthModalProvider')
	}
	return context
}
